import React, { useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Col, Form, Grid, Input, Row, Span } from 'components'
import { SuccessModal } from 'components/SuccessModal/SuccessModal'
import { useForm } from 'react-hook-form'
import { ISindicato } from 'services/NGT/Syndicate'
import { buscaCep } from 'services/Utils/buscaCep'
import { COLORS, SPACING } from 'utils'
import * as yup from 'yup'
import { usePersistForm } from 'hooks'

interface FormProps {
  handleAddNewSyndicate: any
  msgError: string
  loading: any
  isModalOpen: boolean
  toggleModal: () => void
  setLoading: any
}

const schema = yup
  .object({
    cdCep: yup.string().min(9, 'CEP inválido').required('Informe o CEP'),
    cdCnpj: yup
      .string()
      .required('Informe o CNPJ')
      .transform((value) => value.replace(/[^\d]/g, ''))
      .min(14, 'CNPJ inválido'),
    nmBairro: yup
      .string()
      .min(2, 'Bairro inválido.')
      .max(50, 'Máximo 50 caracteres')
      .required('Informe o bairro.'),
    nmSindicato: yup
      .string()
      .min(10, 'Mínimo 10 caracteres')
      .max(100, 'Máximo 100 caracteres')
      .required('Informe o Nome do Sindicato'),
    nuNumero: yup.string().required('Informe o número'),
    sgEstado: yup
      .string()
      .min(2, 'Estado inválido.')
      .max(2, 'Máximo 2 lestras')
      .required('Informe o estado.'),
    sgPais: yup
      .string()
      .min(2, 'País inválido')
      .max(2, 'Máximo 2 lestras')
      .required('Informe o país'),
    txCidade: yup
      .string()
      .min(3, 'Cidade inválida.')
      .max(50, 'Máximo 50 caracteres')
      .required('Informe a cidade.'),
    txComplemento: yup.string().max(50, 'Máximo 50 caracteres'),
    txEndereco: yup
      .string()
      .min(5, 'Mínimo 5 caracteres')
      .required('Informe o Endereço'),
    txObservacao: yup.string().max(100, 'Máximo 100 caracteres')
  })
  .required()

export const SyndicateForm: React.FC<FormProps> = ({
  handleAddNewSyndicate,
  msgError,
  loading,
  setLoading
}) => {
  const { control, watch, handleSubmit, setValue, formState, reset } =
    useForm<ISindicato>({ resolver: yupResolver(schema) })

  const form = watch()

  usePersistForm('ngt_syndicate_form', {
    setValue,
    watch
  })


  const clearForm = () => {
    setValue('cdCnpj', '')
    setValue('nmSindicato', '')
    setValue('cdCep', '')
    setValue('txEndereco', '')
    setValue('nuNumero', '')
    setValue('txComplemento', '')
    setValue('nmBairro', '')
    setValue('txCidade', '')
    setValue('sgEstado', '')
    setValue('sgPais', 'BR')
    setValue('txObservacao', '')
  }

  useEffect(() => {
    clearForm()
  }, [])

  useEffect(() => {
    reset(
      {
        nmBairro: '',
        sgEstado: '',
        txCidade: '',
        txEndereco: ''
      },
      {
        keepValues: true
      }
    )
    setValue('txEndereco', '')
    setValue('nmBairro', '')
    setValue('txCidade', '')
    setValue('sgEstado', '')
    if (form.cdCep?.length > 7) {
      buscaCep(form.cdCep).then(({ data }) => {
        reset(
          {
            nmBairro: '',
            sgEstado: '',
            txCidade: '',
            txEndereco: ''
          },
          {
            keepValues: true
          }
        )
        setValue('txEndereco', data?.logradouro)
        setValue('nmBairro', data?.bairro)
        setValue('txCidade', data?.localidade)
        setValue('sgEstado', data?.uf)
      })
    }
  }, [form.cdCep])

  return (
    <Form
      onSubmit={handleSubmit(handleAddNewSyndicate)}
      marginLeft={SPACING.spacing20}
      marginRight={SPACING.spacing20}
      padding={SPACING.spacing20}
      boxShadow={`0 0 1em ${COLORS.lightGray}`}
    >
      <Grid
        gridCols="150px 316px 150px 1fr"
        gridGap={`0 ${SPACING.spacing10}`}
        margin={`0 0 ${SPACING.spacing30} 0`}
      >
        <Input
          control={control}
          label="CNPJ Sindicato"
          formValue="cdCnpj"
          placeholder="CNPJ"
          errors={formState.errors}
          mask={['##.###.###/####-##']}
          maxLength={18}
          required
        />
        <Input
          control={control}
          label="Sindicato/Confederação"
          formValue="nmSindicato"
          placeholder="Nome"
          errors={formState.errors}
          required
          maxLength={100}
        />
        <Input
          control={control}
          label="CEP"
          formValue="cdCep"
          placeholder="00000-000"
          errors={formState.errors}
          mask={['#####-###']}
          required
          maxLength={9}
        />
        <Input
          control={control}
          label="Endereço"
          formValue="txEndereco"
          value={form.txEndereco}
          placeholder="Preenchido automaticamente após o CEP"
          errors={formState.errors}
          required
          maxLength={50}
        />
      </Grid>
      <Grid
        gridCols="1fr 1fr 2fr 2fr 1fr 1fr"
        gridGap={`0 ${SPACING.spacing10}`}
        margin={`0 0 ${SPACING.spacing30} 0`}
      >
        <Input
          control={control}
          label="Nº do Logradouro"
          formValue="nuNumero"
          placeholder="Nº"
          errors={formState.errors}
          required
          maxLength={10}
        />
        <Input
          control={control}
          label="Complemento"
          formValue="txComplemento"
          placeholder="Complemento"
          maxLength={50}
        />
        <Input
          control={control}
          label="Bairro"
          formValue="nmBairro"
          value={form.nmBairro}
          placeholder="Preenchido automaticamente após o CEP"
          errors={formState.errors}
          required
          maxLength={50}
        />
        <Input
          control={control}
          label="Cidade"
          formValue="txCidade"
          value={form.txCidade}
          placeholder="Preenchido automaticamente após o CEP"
          errors={formState.errors}
          required
          maxLength={50}
        />
        <Input
          control={control}
          label="Estado"
          formValue="sgEstado"
          value={form.sgEstado}
          placeholder="Preenchido automaticamente após o CEP"
          errors={formState.errors}
          required
          maxLength={2}
        />
        <Input
          control={control}
          label="País"
          formValue="sgPais"
          defaultValue="BR"
          errors={formState.errors}
          required
          maxLength={2}
        />
      </Grid>

      <Grid
        gridCols="4fr 4fr"
        gridGap={`0 ${SPACING.spacing10}`}
        margin={`0 0 ${SPACING.spacing30} 0`}
      >
        <Col>
          <Input
            control={control}
            label="Observação"
            formValue="txObservacao"
            type="text"
            placeholder="Observação"
            maxLength={50}
          />
          <Span
            textAlign="end"
            text={String(form.txObservacao?.length) + '/50'}
          />
        </Col>
      </Grid>

      <Row
        justifyContent="flex-end"
        marginTop={SPACING.spacing20}
        marginBottom={SPACING.spacing50}
      >
        <Button
          text="Limpar"
          marginRight={SPACING.spacing20}
          variantButton="text"
          onClick={clearForm}
        />
        <Button text="Salvar" type="submit" marginLeft={SPACING.spacing5} />
      </Row>
      <SuccessModal
        open={loading === 'error'}
        message={msgError}
        type="error"
        hideButtonCancel
        handleConfirm={() => setLoading('idle')}
        txtButtonConfirm="Voltar"
      />
    </Form>
  )
}
